import { useState } from 'react';
import {
  formatDate,
  IconEdit,
  IconRefresh,
  IconChangeLogUpdated,
  IconExpand,
  IconCollapse,
  IconLoader,
} from '@ui/src';
import AmlChangeLogDetails from './change-log-details';
import { AmlChangeLogTypes } from '../types';
import {
  ClientScoreTypeEnum,
  CustomerScore,
  AmlAction,
} from '../../../../hoc/common/types';

import cn from '../styles.module.css';

const AmlChangeLog = ({
  amlChangeLog,
  evenChangeLog,
  userName,
  type,
  riskScoreRanges,
  onCollapse,
  isLoading,
  isFetching,
  applicationAmlDetailsIsLoading,
  applicationAmlDetailsIsFetching,
  selectedChangeLogId,
}: AmlChangeLogTypes) => {
  const [collapsed, setCollapsed] = useState(false);
  const [amlChangeLogDetails, setAmlChangeLogDetails] = useState<
    CustomerScore | undefined
  >(undefined);
  const [evenChangeLogDetails, setEvenChangeLogDetails] = useState<
    CustomerScore | undefined
  >(undefined);

  const onCollapseEvent = async () => {
    const response = await onCollapse({ amlChangeLog, evenChangeLog });

    if (response) {
      if (response?.amlChangeLogDetails) {
        setAmlChangeLogDetails(response?.amlChangeLogDetails);
      }

      if (response?.evenChangeLogDetails) {
        setEvenChangeLogDetails(response?.evenChangeLogDetails);
      }
    }
  };

  let evenTotalRiskClassName: string;
  let amlTotalRiskClassName: string;

  const evenRiskLevel = riskScoreRanges?.find(
    (item) =>
      (evenChangeLog?.totalRiskScore as number) >= item.rangeStart &&
      (evenChangeLog?.totalRiskScore as number) <= item.rangeEnd,
  );

  const amlRiskLevel = riskScoreRanges?.find(
    (item) =>
      amlChangeLog?.totalRiskScore >= item.rangeStart &&
      amlChangeLog?.totalRiskScore <= item.rangeEnd,
  );

  if (evenRiskLevel?.action === AmlAction.NONE) {
    evenTotalRiskClassName = cn.totalRiskScoringGood;
  } else if (evenRiskLevel?.action === AmlAction.OPS_REVIEW) {
    evenTotalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    evenTotalRiskClassName = cn.totalRiskScoringLow;
  }

  if (amlRiskLevel?.action === AmlAction.NONE) {
    amlTotalRiskClassName = cn.totalRiskScoringGood;
  } else if (amlRiskLevel?.action === AmlAction.OPS_REVIEW) {
    amlTotalRiskClassName = cn.totalRiskScoringMedium;
  } else {
    amlTotalRiskClassName = cn.totalRiskScoringLow;
  }

  return (
    <div className={cn.amlChangeLog}>
      <div className={cn.amlChangeLogTop}>
        <div className={cn.amlChangeLogDate}>
          {formatDate(amlChangeLog.createdAt, true)}
        </div>
        {amlChangeLog?.employeeId && (
          <div className={cn.amlChangeLogUser}>{userName}</div>
        )}
        {amlChangeLog?.clientScoreType === ClientScoreTypeEnum.AUTO && (
          <div className={cn.amlChangeLogType}>AUTO</div>
        )}
      </div>
      <div className={cn.amlChangeLogContent}>
        <button
          type="button"
          className={cn.amlChangeLogButton}
          onClick={() => {
            setCollapsed(!collapsed);

            if (!collapsed && !amlChangeLogDetails) {
              onCollapseEvent();
            }
          }}
          disabled={
            amlChangeLog.clientScoreType === ClientScoreTypeEnum.MANUAL &&
            evenChangeLog?.clientScoreType === ClientScoreTypeEnum.MANUAL
          }
          aria-label="Collapse Button"
        >
          <div className={cn.amlChangeLogButtonLeft}>
            {evenChangeLog ? (
              <div className={cn.amlChangeLogPrev}>
                <div
                  className={`${cn.amlChangeLogRiskScore} ${evenTotalRiskClassName}`}
                >
                  {evenChangeLog.totalRiskScore}
                </div>
                <div className={cn.amlChangeLogType}>
                  {evenChangeLog?.clientScoreType ===
                  ClientScoreTypeEnum.AUTO ? (
                    <span>
                      <IconRefresh width={14} height={14} /> auto
                    </span>
                  ) : (
                    <span>
                      <IconEdit width={14} height={14} /> manually
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className={cn.amlPrevEmpty} />
            )}
            <div className={cn.amlChangeLogIconWrapper}>
              <IconChangeLogUpdated />
            </div>
            {amlChangeLog ? (
              <div className={cn.amlChangeLogPrev}>
                <div
                  className={`${cn.amlChangeLogRiskScore} ${amlTotalRiskClassName}`}
                >
                  {amlChangeLog.totalRiskScore}
                </div>
                <div className={cn.amlChangeLogType}>
                  {amlChangeLog?.clientScoreType ===
                  ClientScoreTypeEnum.AUTO ? (
                    <span>
                      <IconRefresh width={14} height={14} /> auto
                    </span>
                  ) : (
                    <span>
                      <IconEdit width={14} height={14} /> manually
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className={cn.amlPrevEmpty} />
            )}
          </div>
          {(amlChangeLog.clientScoreType === ClientScoreTypeEnum.AUTO ||
            evenChangeLog?.clientScoreType === ClientScoreTypeEnum.AUTO) && (
            <div className={cn.amlChangeLogButtonRight}>
              <div className={cn.amlChangeLogButtonRightText}>Details</div>
              <div className={cn.amlChangeLogButtonRightIcon}>
                {!collapsed ? (
                  <IconExpand width={16} height={16} />
                ) : (
                  <IconCollapse width={16} height={16} />
                )}
              </div>
            </div>
          )}
        </button>
        {collapsed && (
          <div className={cn.changeLogsDetails}>
            {selectedChangeLogId === amlChangeLog?.id &&
            (isLoading ||
              isFetching ||
              applicationAmlDetailsIsLoading ||
              applicationAmlDetailsIsFetching) ? (
              <div className={cn.changeLogDetailsLoader}>
                <IconLoader />
              </div>
            ) : (
              <div className={cn.changeLogsDetailsContent}>
                {evenChangeLogDetails ? (
                  <AmlChangeLogDetails
                    changeLog={evenChangeLogDetails}
                    type={type}
                    className={evenTotalRiskClassName}
                    riskLevel={evenRiskLevel}
                  />
                ) : (
                  <div className={cn.changeLogDetailsEmpty}>
                    <div />
                  </div>
                )}
                <IconChangeLogUpdated
                  className={cn.changeLogsDetailsContentArrow}
                />
                {amlChangeLogDetails ? (
                  <AmlChangeLogDetails
                    changeLog={amlChangeLogDetails}
                    type={type}
                    className={amlTotalRiskClassName}
                    riskLevel={amlRiskLevel}
                  />
                ) : (
                  <div className={cn.changeLogDetailsEmpty}>
                    <div />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AmlChangeLog;
